import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function DangerZone() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setPassword(""); setConfirmText(""); setLoading(false); };

  const submit = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Please type "DELETE" exactly to confirm');
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }
    setLoading(true);
    try {
      await api.delete("/auth/account", { data: { password } });
      toast.success("Account deleted.");
      setUser(false);
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="mt-10 border-red-200 bg-red-50/40 p-5" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-100 text-red-700 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold text-red-900">Danger zone</div>
            <div className="text-sm text-red-900/70 mt-0.5">
              Deleting your account removes your sign-in and every resume you've saved. This cannot be undone.
            </div>
          </div>
          <Button
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
            onClick={() => setOpen(true)}
            data-testid="open-delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete account
          </Button>
        </div>
      </Card>

      <Dialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete your account?</DialogTitle>
            <DialogDescription>
              This deletes your sign-in and every resume saved on your account.
              We cannot get them back. To go ahead, type <span className="font-mono font-semibold text-slate-900">DELETE</span> and enter your password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-sm">Type DELETE to confirm</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-1.5"
                data-testid="delete-confirm-input"
              />
            </div>
            <div>
              <Label className="text-sm">Your password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5"
                data-testid="delete-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
